import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { GLASS_CHIP } from './glass';

const BASE = [
  'tw-relative tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden tw-rounded-full tw-font-semibold',
  'tw-transition-[background-color,box-shadow,transform,color] tw-duration-200 active:tw-scale-[0.97]',
  'disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:active:tw-scale-100',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
].join(' ');

const SIZES = {
  sm: 'tw-h-8 tw-px-3 tw-text-xs',
  md: 'tw-h-10 tw-px-4 tw-text-sm',
  lg: 'tw-h-11 tw-px-5 tw-text-sm',
};

// Tinted variants are "liquid glass": a gradient body under a white rim.
const VARIANTS = {
  secondary: `${GLASS_CHIP} tw-text-slate-700 hover:tw-bg-white dark:tw-text-slate-200 dark:hover:tw-bg-white/[0.1]`,
  primary: 'tw-border tw-border-white/40 tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-lg tw-shadow-teal-500/30 hover:tw-shadow-xl hover:tw-shadow-teal-500/40 dark:tw-border-white/20',
  success: 'tw-border tw-border-white/40 tw-bg-gradient-to-b tw-from-emerald-400 tw-to-emerald-600 tw-text-white tw-shadow-lg tw-shadow-emerald-500/30 hover:tw-shadow-xl hover:tw-shadow-emerald-500/40 dark:tw-border-white/20',
  ghost: 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-900/[0.05] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.07]',
  // Destructive but secondary (e.g. "close without saving")
  danger: 'tw-border tw-border-rose-500/25 tw-bg-rose-500/10 tw-text-rose-700 hover:tw-bg-rose-500/15 dark:tw-border-rose-400/20 dark:tw-text-rose-300 dark:hover:tw-bg-rose-500/20',
  // Calls attention without the weight of a filled button (e.g. missing documents)
  warning: 'tw-border tw-border-amber-500/30 tw-bg-amber-400/15 tw-text-amber-800 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] hover:tw-bg-amber-400/25 dark:tw-border-amber-400/25 dark:tw-bg-amber-400/10 dark:tw-text-amber-200 dark:tw-shadow-none dark:hover:tw-bg-amber-400/20',
};

/** Capsule button in the portal's glass style. */
const Button = forwardRef(({ variant = 'secondary', size = 'md', className = '', children, ...rest }, ref) => (
  <button ref={ref} type="button" className={`${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${className}`} {...rest}>
    {children}
  </button>
));

Button.displayName = 'Button';

Button.propTypes = {
  variant: PropTypes.oneOf(Object.keys(VARIANTS)),
  size: PropTypes.oneOf(Object.keys(SIZES)),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Button;
